import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Reveal } from "./motion";
import { IMG } from "../lib/site";
import { useLang } from "../i18n/LanguageContext";

// Signature scroll-driven timeline — the line draws itself as you travel down,
// with imagery that moves from international patient → Indian healthcare → Kerala → home.
export const JourneyTimeline = ({ dark = false }) => {
    const { site: siteData } = useLang();
    const { TIMELINE_STEPS } = siteData;
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start 0.72", "end 0.55"],
    });
    const progress = useSpring(scrollYProgress, { stiffness: 60, damping: 20 });

    return (
        <div ref={ref} className="relative" data-testid="journey-timeline">
            <div
                aria-hidden="true"
                className={`absolute left-[15px] md:left-1/2 md:-translate-x-1/2 top-2 bottom-2 w-px ${
                    dark ? "bg-ivory/15" : "bg-pine/15"
                }`}
            />
            <motion.div
                aria-hidden="true"
                style={{ scaleY: progress }}
                className="absolute left-[15px] md:left-1/2 md:-translate-x-1/2 top-2 bottom-2 w-px bg-natural origin-top"
            />

            <ol className="space-y-10 md:space-y-0">
                {TIMELINE_STEPS.map((step, i) => {
                    const left = i % 2 === 0;
                    return (
                        <li
                            key={step.title}
                            className={`relative pl-12 md:pl-0 md:grid md:grid-cols-2 md:gap-20 ${
                                i > 0 ? "md:pt-14" : ""
                            }`}
                            data-testid={`timeline-step-${i + 1}`}
                        >
                            <span
                                aria-hidden="true"
                                className={`absolute left-[15px] md:left-1/2 md:-translate-x-1/2 top-1.5 md:top-[calc(3.5rem+0.35rem)] w-[9px] h-[9px] rounded-full border-2 ${
                                    dark
                                        ? "bg-forest border-natural"
                                        : "bg-ivory border-natural"
                                }`}
                            />
                            <Reveal
                                className={
                                    left
                                        ? "md:col-start-1 md:pr-4"
                                        : "md:col-start-2 md:pl-4"
                                }
                            >
                                <div
                                    className={`flex items-start gap-5 ${
                                        left ? "md:flex-row-reverse md:text-right" : ""
                                    }`}
                                >
                                    <img
                                        src={IMG[step.img]}
                                        alt={step.imgAlt}
                                        loading="lazy"
                                        data-testid={`timeline-image-${i + 1}`}
                                        className="w-[88px] h-[88px] rounded-xl object-cover border border-pine/10 shadow-sm shrink-0"
                                    />
                                    <div>
                                        <p className="font-serif italic text-lg leading-none text-natural">
                                            {String(i + 1).padStart(2, "0")}
                                        </p>
                                        <h3
                                            className={`font-serif font-medium text-2xl sm:text-[1.7rem] leading-snug mt-1.5 ${
                                                dark ? "text-ivory" : "text-charcoal"
                                            }`}
                                        >
                                            {step.title}
                                        </h3>
                                        <p
                                            className={`text-sm mt-1.5 ${
                                                dark ? "text-ivory/55" : "text-moss"
                                            }`}
                                        >
                                            {step.desc}
                                        </p>
                                    </div>
                                </div>
                            </Reveal>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
};
